import {
  Button,
  Combobox,
  Dialog,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Dropdown,
  Field,
  Input,
  mergeClasses,
  Option,
  OptionGroup,
  Spinner,
  Textarea,
  Tooltip,
} from "@fluentui/react-components";
import {
  ArrowLeftRegular,
  ArrowRightRegular,
  CheckmarkRegular,
  DismissRegular,
} from "@fluentui/react-icons";
import { createTemplate, updateTemplate } from "api";
import { ConstantValues } from "common/constants";
import { CustomToggle } from "components/customToggle/CustomToggle";
import { DialogFooterAction } from "components/dialogFooterAction/DialogFooterAction";
import { DialogTabs } from "components/dialogTabs/DialogTabs";
import { PeoplePicker } from "components/fields/peoplePicker/PeoplePicker";
import { CustomOption } from "components/fields/peoplePicker/PeoplePicker.types";
import { useCategories } from "hooks/useCategories";
import { IAccessLLMModel } from "models/IAccessLLMModel";
import { ICategory } from "models/ICategory";
import { ITag } from "models/ITag";
import { IUser } from "models/IUser";
import { ChangeEvent, useEffect, useMemo, useState } from "react";
import { useQueryClient } from "react-query";
import { useSelector } from "react-redux";
import { RootState } from "store";
import {
  useSharedDialogFooterStyles,
  useSharedDropdownStyles,
  useSharedDialogContentStyles,
} from "theme/sharedStyles";
import { getPreselectedUsers, getFieldValidation } from "utils/dialogUtils";
import { getKnowledgeComponentsLabel } from "components/manageUseCasePrompts/useCasePromptUtils";
import { templateIconOptions } from "utils/templateIcons";
import { useEditTemplateDialogStyles } from "./EditTemplateDialog.styles";
import {
  DIALOG_LABELS,
  FIELD_LABELS,
  FIELD_LIMITS,
  IEditTemplateDialogProps,
} from "./EditTemplateDialog.types";

const TabValues = {
  DETAILS: "details",
  CONFIGURATION: "configuration",
} as const;

export const EditTemplateDialog = ({
  template,
  isOpen,
  onClose,
  onSuccess,
}: IEditTemplateDialogProps): JSX.Element => {
  const isCreateMode = !template;

  const [name, setName] = useState<string>(
    template?.name || ConstantValues.EMPTY_STRING,
  );
  const [icon, setIcon] = useState<string>(
    template?.icon || ConstantValues.EMPTY_STRING,
  );
  const [description, setDescription] = useState<string>(
    template?.description || ConstantValues.EMPTY_STRING,
  );
  const [recommendedLLM, setRecommendedLLM] = useState<string>(
    template?.recommendedLLM || ConstantValues.EMPTY_STRING,
  );
  const [prefilledPrompt, setPrefilledPrompt] = useState<string>(
    template?.prefilledPrompt || ConstantValues.EMPTY_STRING,
  );
  const [selectedKnowledgeComponents, setSelectedKnowledgeComponents] =
    useState<string[]>(template?.suggestedKnowledgeComponents || []);
  const [isActive, setIsActive] = useState<boolean>(template?.isActive ?? true);
  const [showOnChatScreen, setShowOnChatScreen] = useState<boolean>(
    template?.showOnChatScreen ?? true,
  );
  const [accessControl, setAccessControl] = useState<"limited" | "full">(
    template?.accessControl === "disabled"
      ? "full"
      : (template?.accessControl as "limited" | "full") || "full",
  );
  const [allowedUsers, setAllowedUsers] = useState<CustomOption[]>([]);
  const [selectedTab, setSelectedTab] = useState<string>(TabValues.DETAILS);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const styles = useEditTemplateDialogStyles();
  const contentStyles = useSharedDialogContentStyles();
  const dropdownStyles = useSharedDropdownStyles();
  const footerStyles = useSharedDialogFooterStyles();
  const queryClient = useQueryClient();
  const selectedIconOption = useMemo(
    () => templateIconOptions.find((o) => o.key === icon),
    [icon],
  );

  const models: IAccessLLMModel[] = useSelector(
    (state: RootState) => state.accessLLMModelsSlice.value,
  );

  const users: IUser[] = useSelector(
    (state: RootState) => state.usersSlice.value,
  );

  const { data: categories } = useCategories();

  const selectedKnowledgeComponentsLabel = getKnowledgeComponentsLabel(
    selectedKnowledgeComponents,
    categories || [],
  );

  const handleTabClick = (tab: string): void => {
    if (tab === selectedTab) return;
    if (!validateCurrentTab()) return;
    setSelectedTab(tab);
  };

  const handleNextTab = () => {
    if (!validateCurrentTab()) return;
    setSelectedTab(TabValues.CONFIGURATION);
  };

  const handlePrevTab = () => {
    setSelectedTab(TabValues.DETAILS);
  };

  const isDetailsTabComplete =
    icon.trim() !== ConstantValues.EMPTY_STRING &&
    name.trim() !== ConstantValues.EMPTY_STRING &&
    name.length <= FIELD_LIMITS.NAME_MAX_LENGTH &&
    description.trim() !== ConstantValues.EMPTY_STRING &&
    description.length <= FIELD_LIMITS.DESCRIPTION_MAX_LENGTH;

  const isConfigurationTabComplete =
    recommendedLLM.trim() !== ConstantValues.EMPTY_STRING;

  const isAllTabsComplete = isDetailsTabComplete && isConfigurationTabComplete;

  const isFirstTab = selectedTab === TabValues.DETAILS;
  const isLastTab = selectedTab === TabValues.CONFIGURATION;

  const validateCurrentTab = (): boolean => {
    const errors: Record<string, string> = {};
    if (selectedTab === TabValues.DETAILS) {
      if (!icon.trim()) errors.icon = "Icon is required";
      if (!name.trim()) errors.name = "Title is required";
      if (!description.trim()) errors.description = "Description is required";
    }

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return false;
    }

    return true;
  };

  const handleSave = async () => {
    const errors: Record<string, string> = {};
    if (!icon.trim()) errors.icon = "Icon is required";
    if (!name.trim()) errors.name = "Title is required";
    if (name.length > FIELD_LIMITS.NAME_MAX_LENGTH)
      errors.name = `Title must be ${FIELD_LIMITS.NAME_MAX_LENGTH} characters or fewer.`;
    if (!description.trim()) errors.description = "Description is required";
    if (description.length > FIELD_LIMITS.DESCRIPTION_MAX_LENGTH)
      errors.description = `Description must be ${FIELD_LIMITS.DESCRIPTION_MAX_LENGTH} characters or fewer.`;
    if (!recommendedLLM.trim())
      errors.recommendedLLM = "Recommended LLM is required";

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }

    setFieldErrors({});
    setIsLoading(true);
    try {
      if (isCreateMode) {
        await createTemplate({
          name,
          icon,
          description: description || undefined,
          recommendedLLM,
          prefilledPrompt: prefilledPrompt || undefined,
          suggestedKnowledgeComponents: selectedKnowledgeComponents,
          isActive,
          showOnChatScreen,
          accessControl,
          allowedUsers: allowedUsers.map((u) => u.id || u.key),
        });
      } else {
        await updateTemplate(template!.id, {
          name,
          icon,
          description: description || undefined,
          recommendedLLM,
          prefilledPrompt: prefilledPrompt || undefined,
          suggestedKnowledgeComponents: selectedKnowledgeComponents,
          isActive,
          showOnChatScreen,
          accessControl,
          allowedUsers: allowedUsers.map((u) => u.id || u.key),
        });
      }

      await Promise.all([
        queryClient.invalidateQueries("templates"),
        queryClient.invalidateQueries("templates-admin"),
        queryClient.invalidateQueries("chat-screen-templates"),
      ]);

      onSuccess();
      onClose();
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "An unexpected error occurred";
      setFieldErrors((prev) => ({
        ...prev,
        general: `Failed to ${isCreateMode ? "create" : "update"} template: ${errorMessage}`,
      }));
      console.error(
        `Failed to ${isCreateMode ? "create" : "update"} template:`,
        err,
      );
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      setName(template?.name || ConstantValues.EMPTY_STRING);
      setIcon(template?.icon || ConstantValues.EMPTY_STRING);
      setDescription(template?.description || ConstantValues.EMPTY_STRING);
      setRecommendedLLM(
        template?.recommendedLLM || ConstantValues.EMPTY_STRING,
      );
      setPrefilledPrompt(
        template?.prefilledPrompt || ConstantValues.EMPTY_STRING,
      );
      setSelectedKnowledgeComponents(
        template?.suggestedKnowledgeComponents || [],
      );
      setIsActive(template?.isActive ?? true);
      setShowOnChatScreen(template?.showOnChatScreen ?? true);
      setAccessControl(
        template?.accessControl === "disabled"
          ? "full"
          : (template?.accessControl as "limited" | "full") || "full",
      );
      setAllowedUsers(getPreselectedUsers(template?.allowedUsers || [], users));
      setSelectedTab(TabValues.DETAILS);
      setFieldErrors({});
    }
  }, [isOpen, template, users]);

  const nameValidation = getFieldValidation(
    name.length,
    FIELD_LIMITS.NAME_MAX_LENGTH,
    FIELD_LIMITS.NAME_WARNING_THRESHOLD,
  );
  const descValidation = getFieldValidation(
    description.length,
    FIELD_LIMITS.DESCRIPTION_MAX_LENGTH,
    FIELD_LIMITS.DESCRIPTION_WARNING_THRESHOLD,
  );

  return (
    <Dialog
      modalType="modal"
      open={isOpen}
      onOpenChange={(_, data) => !data.open && onClose()}
    >
      <DialogSurface
        className={mergeClasses(
          styles.dialogSurface,
          contentStyles.dialogSurface,
        )}
      >
        <DialogBody>
          <DialogTitle
            action={
              <Button
                appearance="subtle"
                aria-label="Close"
                icon={<DismissRegular />}
                onClick={onClose}
                disabled={isLoading}
              />
            }
          >
            {isCreateMode ? DIALOG_LABELS.CREATE : DIALOG_LABELS.EDIT}
          </DialogTitle>
          <DialogContent
            className={mergeClasses(
              contentStyles.tabbedDialogContent,
              styles.dialogContent,
            )}
          >
            <DialogTabs
              tabs={[
                { key: TabValues.DETAILS, label: "Details" },
                { key: TabValues.CONFIGURATION, label: "Configuration" },
              ]}
              activeTab={selectedTab}
              onTabClick={handleTabClick}
            />
            <div
              className={mergeClasses(
                contentStyles.formContainer,
                contentStyles.scrollableContent,
              )}
            >
              {isLoading && (
                <div className={contentStyles.formLoadingOverlay}>
                  <Spinner label="Saving..." />
                </div>
              )}
              {selectedTab === TabValues.DETAILS && (
                <div className={contentStyles.tabContent}>
                  <Field
                    label={FIELD_LABELS.ICON}
                    required
                    validationState={fieldErrors.icon ? "error" : "none"}
                    validationMessage={fieldErrors.icon}
                  >
                    <Dropdown
                      className={dropdownStyles.dropdown}
                      listbox={{
                        className: dropdownStyles.listbox,
                      }}
                      placeholder="Select one item"
                      positioning="below"
                      value={selectedIconOption?.label || icon}
                      button={
                        icon
                          ? {
                              children: (
                                <span className={styles.iconDropdownValue}>
                                  {selectedIconOption?.icon ?? null}
                                  {selectedIconOption?.label || icon}
                                </span>
                              ),
                            }
                          : undefined
                      }
                      selectedOptions={[icon]}
                      onOptionSelect={(_, data) => {
                        if (data.optionValue) {
                          setIcon(data.optionValue);
                          if (fieldErrors.icon)
                            setFieldErrors((prev) => ({
                              ...prev,
                              icon: ConstantValues.EMPTY_STRING,
                            }));
                        }
                      }}
                      disabled={isLoading}
                    >
                      {templateIconOptions.map((opt) => (
                        <Option key={opt.key} value={opt.key} text={opt.label}>
                          {opt.icon} {opt.label}
                        </Option>
                      ))}
                    </Dropdown>
                  </Field>

                  <Field
                    label={FIELD_LABELS.TITLE}
                    required
                    validationState={
                      fieldErrors.name ? "error" : nameValidation.state
                    }
                    validationMessage={
                      fieldErrors.name || nameValidation.message
                    }
                  >
                    <Input
                      value={name}
                      onChange={(e: ChangeEvent<HTMLInputElement>) => {
                        setName(e.target.value);
                        if (fieldErrors.name)
                          setFieldErrors((prev) => ({
                            ...prev,
                            name: ConstantValues.EMPTY_STRING,
                          }));
                      }}
                      placeholder="Template Name"
                      disabled={isLoading}
                    />
                  </Field>

                  <Field
                    label={FIELD_LABELS.DESCRIPTION}
                    required
                    validationState={
                      fieldErrors.description ? "error" : descValidation.state
                    }
                    validationMessage={
                      fieldErrors.description || descValidation.message
                    }
                  >
                    <Input
                      value={description}
                      onChange={(e: ChangeEvent<HTMLInputElement>) => {
                        setDescription(e.target.value);
                        if (fieldErrors.description)
                          setFieldErrors((prev) => ({
                            ...prev,
                            description: ConstantValues.EMPTY_STRING,
                          }));
                      }}
                      placeholder="Short description shown as tooltip on chat screen"
                      disabled={isLoading}
                    />
                  </Field>

                  <Field label={FIELD_LABELS.PROMPT}>
                    <Textarea
                      className={styles.textareaField}
                      value={prefilledPrompt}
                      onChange={(e: ChangeEvent<HTMLTextAreaElement>) => {
                        setPrefilledPrompt(e.target.value);
                      }}
                      placeholder="Optional prompt to pre-fill when using this template..."
                      rows={1}
                      disabled={isLoading}
                    />
                  </Field>
                </div>
              )}

              {selectedTab === TabValues.CONFIGURATION && (
                <div className={contentStyles.tabContent}>
                  <Field
                    label={FIELD_LABELS.RECOMMENDED_LLM}
                    required
                    validationState={
                      fieldErrors.recommendedLLM ? "error" : "none"
                    }
                    validationMessage={fieldErrors.recommendedLLM}
                  >
                    <Dropdown
                      className={dropdownStyles.dropdown}
                      listbox={{
                        className: dropdownStyles.listbox,
                      }}
                      placeholder="Select an LLM model"
                      positioning="below"
                      value={recommendedLLM}
                      selectedOptions={[recommendedLLM]}
                      onOptionSelect={(_, data) => {
                        if (data.optionValue) {
                          setRecommendedLLM(data.optionValue);
                          if (fieldErrors.recommendedLLM)
                            setFieldErrors((prev) => ({
                              ...prev,
                              recommendedLLM: ConstantValues.EMPTY_STRING,
                            }));
                        }
                      }}
                      disabled={isLoading}
                    >
                      {[...models]
                        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
                        .map((model) => {
                          const displayName = model.displayName || model.name;
                          return (
                            <Option key={model.id} value={displayName}>
                              {displayName}
                            </Option>
                          );
                        })}
                    </Dropdown>
                  </Field>

                  <Field label={FIELD_LABELS.KNOWLEDGE_COMPONENTS}>
                    <Tooltip
                      content={
                        selectedKnowledgeComponentsLabel ||
                        "Select knowledge components"
                      }
                      relationship="label"
                    >
                      <Combobox
                        multiselect
                        placeholder="Select knowledge components"
                        selectedOptions={selectedKnowledgeComponents}
                        value={selectedKnowledgeComponentsLabel}
                        onChange={() => {}}
                        freeform={false}
                        positioning="below"
                        onOptionSelect={(_, data) => {
                          setSelectedKnowledgeComponents(data.selectedOptions);
                        }}
                        className={styles.knowledgeComponentCombobox}
                        listbox={{
                          className: dropdownStyles.listbox,
                        }}
                        disabled={isLoading}
                      >
                        {(categories || []).map((category: ICategory) => (
                          <OptionGroup key={category.id} label={category.title}>
                            {category.tags.map((tag: ITag) => (
                              <Option
                                key={tag.id}
                                value={tag.id}
                                className={styles.comboboxOption}
                              >
                                {tag.name}
                              </Option>
                            ))}
                          </OptionGroup>
                        ))}
                      </Combobox>
                    </Tooltip>
                  </Field>

                  <CustomToggle
                    checked={isActive}
                    onChange={setIsActive}
                    label="Active"
                    hint={
                      isActive
                        ? "Template is active and visible to users"
                        : "Template is inactive and hidden from users"
                    }
                    disabled={isLoading}
                  />

                  <CustomToggle
                    checked={showOnChatScreen}
                    onChange={setShowOnChatScreen}
                    label="Show on Chat Screen"
                    hint={
                      showOnChatScreen
                        ? "Template will appear on blank chat screen"
                        : "Template will only appear in gallery"
                    }
                    disabled={isLoading}
                  />

                  <Field
                    label={FIELD_LABELS.ACCESS_CONTROL}
                    required
                    validationState={
                      fieldErrors.accessControl ? "error" : "none"
                    }
                    validationMessage={fieldErrors.accessControl}
                  >
                    <Dropdown
                      className={dropdownStyles.dropdown}
                      listbox={{
                        className: dropdownStyles.listbox,
                      }}
                      placeholder="Select access level"
                      positioning="below"
                      value={
                        accessControl === "full"
                          ? "Full - All users can access"
                          : "Limited - Specific users only"
                      }
                      selectedOptions={[accessControl]}
                      onOptionSelect={(_, data) => {
                        if (data.optionValue) {
                          const value = data.optionValue as "limited" | "full";
                          setAccessControl(value);
                          if (value !== "limited") {
                            setAllowedUsers([]);
                          }
                        }
                      }}
                      aria-label="Access control"
                      disabled={isLoading}
                    >
                      <Option value="full">Full - All users can access</Option>
                      <Option value="limited">
                        Limited - Specific users only
                      </Option>
                    </Dropdown>
                  </Field>

                  {accessControl === "limited" && (
                    <Field label={FIELD_LABELS.SELECTED_USERS}>
                      <PeoplePicker
                        selectedOptions={allowedUsers}
                        handleSelectedItems={setAllowedUsers}
                        hasOnlyOurUsers
                        disabled={isLoading}
                      />
                      <div className={styles.allowedUsersHelp}>
                        Users:{" "}
                        {allowedUsers.length > 0 ? allowedUsers.length : "None"}
                      </div>
                    </Field>
                  )}

                  {fieldErrors.general && (
                    <div
                      className={contentStyles.feedbackErrorBox}
                      role="alert"
                    >
                      {fieldErrors.general}
                    </div>
                  )}
                </div>
              )}
            </div>
          </DialogContent>
        </DialogBody>
        {!isLoading && (
          <div className={footerStyles.dialogFooter}>
            {isLastTab && (
              <DialogFooterAction
                icon={<ArrowLeftRegular />}
                onClick={handlePrevTab}
                tooltip="Back"
              />
            )}
            {isFirstTab && (
              <DialogFooterAction
                icon={<ArrowRightRegular />}
                onClick={handleNextTab}
                dimmed={!isDetailsTabComplete}
                tooltip="Next"
              />
            )}
            {isLastTab && (
              <DialogFooterAction
                icon={<CheckmarkRegular />}
                onClick={handleSave}
                dimmed={!isAllTabsComplete}
                tooltip="Save"
              />
            )}
          </div>
        )}
      </DialogSurface>
    </Dialog>
  );
};
